import { Flex, Modal } from "@mantine/core";
import { DateInput, TimePicker } from "@mantine/dates";
import React from "react";

import { IMeeting } from "@mctypes/meeting.types";
import { useHistory } from "react-router-dom";
import { FailedAlert, McTitle } from "../../../../components";
import { ColorDropdown } from "../../../../components/ColorDropDown";
import McDropdownNew from "../../../../components/McDropdown/MCDropdownNew";
import McInputNew from "../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../components/McInput/McTextAreaNew";
import { getProjectIdFromUrl } from "../../../utils";
import { useGetProjectByProjectIdIssueboardQuery } from "../../../../../openapi";
import {
  MEETING_INTERVALS,
  MEETINGS_SERIES_STATUSES,
} from "../../projectdashboard/MeetingSeries/meetingSeriesConstants";
import { useUpdateMeetingSerieItemMutation } from "../meetingSerieRTK";

const toDateInputValue = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toYyyyMmDd = (value: Date | string | null): string => {
  if (!value) return "";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const MeetingSerieItemModal: React.FC<{
  meetingSerie: IMeeting.MeetingSerie;
  toggle: () => void;
}> = ({ meetingSerie, toggle }) => {
  const [updateMeetingSerie, { isError: updateError }] = useUpdateMeetingSerieItemMutation();
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);

  const { data: issueBoards = [] } = useGetProjectByProjectIdIssueboardQuery({
    projectId: projectId,
  });

  const onChange: IMeeting.onMeetingSerieItemChange = (oldValue, newValue, param) => {
    updateMeetingSerie({
      projectId: projectId,
      meetingSerieId: meetingSerie._id,
      attribute: param,
      value: newValue,
    });
  };

  return (
    <Modal opened onClose={toggle} title={meetingSerie?.name}>
      {updateError === true && <FailedAlert />}
      {meetingSerie != null && (
        <Flex direction="column" gap="xs">
          <McTitle title="Status">
            <McDropdownNew
              currentValue={MEETINGS_SERIES_STATUSES.find((item) => item.value === meetingSerie.status)}
              displayKey="label"
              options={MEETINGS_SERIES_STATUSES}
              onChange={(oldVal, newObj) => onChange(null, newObj.value as any, "status")}
            />
          </McTitle>
          <McTitle title="Tittel">
            <McInputNew onBlur={(e) => onChange(meetingSerie.name, e.target.value, "name")} value={meetingSerie.name} />
          </McTitle>
          <McTitle title="Beskrivelse">
            <McTextAreaNew
              spellCheck={false}
              autosize
              onBlur={(e) => onChange(meetingSerie.description, e.target.value, "description")}
              value={meetingSerie.description}
            />
          </McTitle>
          <McTitle title="Interval">
            <McDropdownNew
              currentValue={MEETING_INTERVALS.find((item) => item.id === String(meetingSerie.interval))}
              displayKey="name"
              options={MEETING_INTERVALS}
              onChange={(oldVal, newObj) => onChange(null, newObj.id, "interval")}
            />
          </McTitle>
          <McTitle title="Startdato">
            <DateInput
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              valueFormat="DD.MM.YYYY"
              value={toDateInputValue(meetingSerie.seriesStart)}
              onChange={(value) => {
                onChange(meetingSerie.seriesStart, toYyyyMmDd(value), "seriesStart");
              }}
            />
          </McTitle>
          <McTitle title="Til dato">
            <DateInput
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              clearable
              valueFormat="DD.MM.YYYY"
              value={toDateInputValue(meetingSerie.seriesEnd)}
              onChange={(value) => {
                onChange(meetingSerie.seriesEnd, toYyyyMmDd(value), "seriesEnd");
              }}
            />
          </McTitle>
          <McTitle title="Starttid">
            <TimePicker
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              format="24h"
              withSeconds={false}
              withDropdown
              value={meetingSerie.timeStart ?? ""}
              onChange={(value) => {
                if ((meetingSerie.timeStart ?? "") === value) return;
                onChange(meetingSerie.timeStart, value, "timeStart");
              }}
            />
          </McTitle>
          <McTitle title="Sluttid">
            <TimePicker
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              format="24h"
              withSeconds={false}
              withDropdown
              value={meetingSerie.timeEnd ?? ""}
              onChange={(value) => {
                if ((meetingSerie.timeEnd ?? "") === value) return;
                onChange(meetingSerie.timeEnd, value, "timeEnd");
              }}
            />
          </McTitle>
          <McTitle title="Farge">
            <ColorDropdown
              activeColor={meetingSerie.color}
              setColor={(color) => onChange(meetingSerie.color, color, "color")}
            />
          </McTitle>
          <McTitle title="Standard saksregister">
            <McDropdownNew
              disabled={issueBoards.length === 0}
              onClear={
                meetingSerie.defaultIssueBoard != null
                  ? () => onChange(meetingSerie.defaultIssueBoard as any, null as any, "defaultIssueBoard")
                  : undefined
              }
              currentValue={meetingSerie.defaultIssueBoard}
              onChange={(oldVal, newVal) => onChange(null, newVal, "defaultIssueBoard")}
              options={issueBoards.map((board) => ({ name: board.name, _id: board._id })) ?? []}
              displayKey="name"
            />
          </McTitle>
        </Flex>
      )}
    </Modal>
  );
};

export default MeetingSerieItemModal;


